import qs from 'qs';

import { ServiceError } from 'src/lib/errors';
import { config } from 'src/lib/config';
import { AbstractService } from 'src/services/AbstractService';

class RelevanceTestsApi extends AbstractService implements Services.RelevanceTestsApi.RelevanceTests {
    public async getRelevanceTestDefinitions(
        bearerToken: string | undefined | false,
        search: App.RelevanceTestDefinitions.Search,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinitions. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            ...search,
            offset,
            limit,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definitions',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async getRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async createRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        relevanceTestDefinition: Models.IngestConfigApi.RelevanceTests.CreateRelevanceTestDefinition,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request createRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    json: {
                        ...relevanceTestDefinition,
                    },
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to create relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async updateRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        relevanceTestDefinition: Models.IngestConfigApi.RelevanceTests.UpdateRelevanceTestDefinition,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request updateRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .put(url, {
                    searchParams,
                    json: relevanceTestDefinition,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestDefinition>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to update relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async deleteRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        signal?: AbortSignal,
    ): Promise<void> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request deleteRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            await this.api
                .delete(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<void>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to delete relevance test definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async runRelevanceTest(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        options?: {
            searchBackend?: string;
            experimentFlags?: Record<string, string>;
        },
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request runRelevanceTest. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        const requestBody = {
            searchBackend: options?.searchBackend || 'prod',
            experimentFlags: options?.experimentFlags || {},
        };

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    signal,
                    json: requestBody,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to run relevance test',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async getTestRunsForDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getTestRunsForDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            offset,
            limit,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.PageResult<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun>>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get test runs for definition',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async runAllRelevanceTests(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun[]> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request runAllRelevanceTests. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceTestRun[]>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to run all relevance tests',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async exportLatestRelevanceTestRuns(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<Blob> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request exportLatestRelevanceTestRuns. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/testRun/export`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const foo = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },

                });

            return await foo.blob();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to export latest relevance test runs',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async getRelevanceTestDefinitionIds(
        bearerToken: string | undefined | false,
        signal?: AbortSignal,
    ): Promise<string[]> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getRelevanceTestDefinitionIds. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/ids`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<string[]>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get relevance test definition IDs',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async compareRelevanceTestDefinition(
        bearerToken: string | undefined | false,
        testDefinitionId: string,
        experimentFlags: Record<string, string>,
        searchBackend: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.RelevanceTests.RelevanceComparisonResult> {
        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request compareRelevanceTestDefinition. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/testDefinition/${testDefinitionId}/compare`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api
                .post(url, {
                    timeout: 60000,
                    json: {
                        experimentFlags,
                        searchBackend,
                    },
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                .json<Models.IngestConfigApi.RelevanceTests.RelevanceComparisonResult>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: `Failed to compare relevance test definition ${testDefinitionId}`,
                info: (e as Error).message,
                url,
            });
        }
    }
}

export const RelevanceTestsService = new RelevanceTestsApi(config.services.ingestConfigServiceApi);
